import React from "react";

export interface TagItem {
  id: string | number;
  name: string;
  slug: string;
  listingName?: string;
}

export interface ScrollingTagListProps {
  items: TagItem[];
  baseHref: string;
}

export const ScrollingTagList = (props: ScrollingTagListProps) => {
  if (!props.items || 0 === props.items.length) {
    return null; // Don't render anything if there are no items
  }

  // Duplicate items for seamless scrolling effect
  const displayItems = [...props.items, ...props.items];

  // Calculate dynamic animation duration for a slower, consistent perceived speed.
  // Base: 5 seconds per original item. Minimum duration: 10 seconds.
  const calculatedDurationSeconds = props.items.length / 2;
  const finalAnimationDuration = `${Math.max(10, calculatedDurationSeconds)}s`;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SCROLLING_LIST_STYLES }} />
      <div
        className="scrolling-tag-outer-container"
        aria-label="Scrolling list of categories"
      >
        <div
          className="scrolling-tag-inner-container"
          style={{ animationDuration: finalAnimationDuration }} // Apply dynamic duration
        >
          {displayItems.map((item, index) => (
            <a
              key={`${item.id}-${index}`}
              href={`${props.baseHref}${item.slug}/`}
              className="tag-item bg-brand-100 text-brand-700 hover:bg-brand-200 shadow-brand-800/15 border-brand-300 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-150 ease-in-out hover:shadow-md"
            >
              {item.listingName || item.name}
            </a>
          ))}
        </div>
      </div>
    </>
  );
};

export const SCROLLING_LIST_STYLES = `
.scrolling-tag-outer-container {
  overflow: hidden;
  position: relative;
  width: 100%;
  /* Height for 3 lines of tags (approx 2.25rem each) + 2 gaps (1rem each) */
  /* (3 * 2.25rem) + (2 * 1rem) = 6.75rem + 2rem = 8.75rem */
  height: 8.75rem;
  /* Fade out edges for a smoother look */
  -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
}

.scrolling-tag-inner-container {
  display: flex;
  flex-direction: column; /* Items stack vertically first */
  flex-wrap: wrap;       /* Then wrap into new columns to fill height */
  gap: 1rem;             /* Matches original gap-4, applies to rows and columns here */
  width: max-content;    /* Essential for scrolling behavior with columns */
  height: 100%;          /* Fill the outer container's height, critical for column wrapping */
  margin: 0 2rem;
  animation-name: scrollEffect;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  /* animation-duration is now set via inline style */
}

.scrolling-tag-outer-container:hover .scrolling-tag-inner-container {
  animation-play-state: paused;
}

@keyframes scrollEffect {
  0% {
    transform: translateX(0%);
  }
  100% {
    /* Since content is duplicated, scrolling by -50% of its total width 
       means scrolling exactly the width of the original set of items (now arranged in columns). */
    transform: translateX(-50%);
  }
}

.tag-item {
  flex-shrink: 0; /* Prevent tags from shrinking */
  white-space: nowrap; /* Ensure tag text doesn't wrap */
  height: fit-content; /* Ensure tags don't try to stretch to fill column space if only 1 or 2 items */
}
`;
